import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, Check, Play, Users, Building2, User, AlertCircle, IndianRupee } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/formatters';
import { useEmployees, useDepartments } from '@/api/queries/employee.queries';
import { useSalaryStructures, useRunPayroll, usePayrollSettings } from '@/api/queries/payroll.queries';

const STEPS = ['Period', 'Select Employees', 'Review & Run'];

function StepBar({ step }) {
  return (
    <div className="flex items-center">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-semibold transition-colors ${i === step ? 'bg-[var(--accent)] text-white' : i < step ? 'bg-green-100 dark:bg-green-900/30 text-green-700' : 'bg-[var(--bg-muted)] text-[var(--text-tertiary)]'}`}>
            {i < step ? <Check size={12}/> : <span>{i+1}</span>} {s}
          </div>
          {i < STEPS.length-1 && <div className="w-5 h-px bg-[var(--border)] mx-1"/>}
        </div>
      ))}
    </div>
  );
}

export default function PayrollRunPage() {
  const navigate = useNavigate();
  const runPayroll = useRunPayroll();
  const { data: empResp }  = useEmployees({ status: 'ACTIVE', limit: 200 });
  const { data: ssResp }   = useSalaryStructures({ limit: 200 });
  const { data: deptResp } = useDepartments();
  const { data: settings } = usePayrollSettings();

  const employees = (empResp?.data || []).filter(e => e.status === 'ACTIVE' || !e.status);
  const structures = ssResp?.data || [];
  const departments = (deptResp?.data || []).map(d => d.name || d);
  const cfg = settings?.data;

  const lastMonth = subMonths(new Date(), 1);
  const [step, setStep] = useState(0);
  const [month, setMonth] = useState(format(lastMonth, 'yyyy-MM'));
  const [selection, setSelection] = useState('all'); // all | department | designation | custom
  const [selDept, setSelDept] = useState('');
  const [selDesig, setSelDesig] = useState('');
  const [customIds, setCustomIds] = useState(new Set());
  const [done, setDone] = useState(false);
  const [newRunId, setNewRunId] = useState(null);

  const periodStart = format(startOfMonth(new Date(month + '-01')), 'yyyy-MM-dd');
  const periodEnd   = format(endOfMonth(new Date(month + '-01')), 'yyyy-MM-dd');
  const payPeriod   = format(new Date(month + '-01'), 'MMMM yyyy');

  // Build salary structure map
  const ssMap = useMemo(() => {
    const m = {};
    structures.forEach(s => { m[s.employee_id] = s; });
    return m;
  }, [structures]);

  // Determine which employees are selected
  const selectedEmployees = useMemo(() => {
    let base = employees;
    if (selection === 'department' && selDept) base = employees.filter(e => (typeof e.department === 'string' ? e.department : e.department?.name) === selDept);
    if (selection === 'designation' && selDesig) base = employees.filter(e => (typeof e.designation === 'string' ? e.designation : e.designation?.name) === selDesig);
    if (selection === 'custom') base = employees.filter(e => customIds.has(e.id || e.employee_id));
    return base;
  }, [employees, selection, selDept, selDesig, customIds]);

  // Which selected employees have salary structures
  const withSS  = selectedEmployees.filter(e => ssMap[e.id || e.employee_id]);
  const withoutSS = selectedEmployees.filter(e => !ssMap[e.id || e.employee_id]);

  // Aggregate preview totals
  const totals = useMemo(() => {
    return withSS.reduce((acc, e) => {
      const ss = ssMap[e.id || e.employee_id];
      const gross = Number(ss.gross_monthly);
      const basic = Number(ss.basic);
      const pfEmp = ss.pf_applicable ? Math.min(basic, 15000) * 0.12 : 0;
      const esic  = gross <= 21000 ? gross * 0.0075 : 0;
      const pt    = gross > 10000 ? 200 : gross > 7500 ? 175 : 0;
      const net   = gross - pfEmp - esic - pt;
      return {
        gross: acc.gross + gross,
        pf: acc.pf + pfEmp,
        esic: acc.esic + esic,
        pt: acc.pt + pt,
        net: acc.net + net,
      };
    }, { gross: 0, pf: 0, esic: 0, pt: 0, net: 0 });
  }, [withSS, ssMap]);

  const allDesignations = [...new Set(employees.map(e => typeof e.designation === 'string' ? e.designation : e.designation?.name).filter(Boolean))].sort();

  const toggleCustom = (id) => setCustomIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleRun = async () => {
    if (withSS.length === 0) return toast.error('No employees with salary structures in selection');
    try {
      const payload = {
        pay_period: payPeriod,
        period_start: periodStart,
        period_end: periodEnd,
        employee_ids: selection !== 'all' ? withSS.map(e => e.id || e.employee_id) : undefined,
        department: selection === 'department' ? selDept : undefined,
        designation: selection === 'designation' ? selDesig : undefined,
      };
      const result = await runPayroll.mutateAsync(payload);
      setNewRunId(result?.data?.id);
      setDone(true);
    } catch(e) {
      toast.error(e?.response?.data?.error?.message || 'Payroll run failed');
    }
  };

  const inp = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  // ── Done screen ──
  if (done) return (
    <div className="max-w-md mx-auto">
      <div className="card text-center py-14 space-y-4">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
          <Check size={32} className="text-green-600"/>
        </div>
        <div>
          <p className="text-[22px] font-black text-[var(--text-primary)]">Payroll Processed!</p>
          <p className="text-[13px] text-[var(--text-secondary)] mt-1">{payPeriod} · {withSS.length} employees · {formatCurrency(totals.net)} net</p>
        </div>
        <div className="flex gap-3 justify-center pt-2">
          <Button onClick={() => navigate(newRunId ? `/payroll/${newRunId}` : '/payroll')}>View Payslips</Button>
          <Button variant="outline" onClick={() => navigate('/payroll')}>Back to Payroll</Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl space-y-5 fade-in">
      <PageHeader
        title="Run Payroll"
        breadcrumb={[{ label: 'Payroll', href: '/payroll' }, { label: 'Run Payroll' }]}
        actions={<button onClick={() => navigate('/payroll')} className="flex items-center gap-1.5 text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><ArrowLeft size={14}/> Back</button>}
      />

      {/* Warning if no bank settings */}
      {!cfg?.bank_name && (
        <div className="flex items-center gap-3 p-3 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-[12px] text-amber-700 dark:text-amber-400">
          <AlertCircle size={14} className="shrink-0"/>
          Company bank details not configured. <button className="underline font-semibold ml-1" onClick={() => navigate('/settings/payroll-bank')}>Set up now →</button>
        </div>
      )}

      <StepBar step={step}/>

      {/* ── STEP 0: Period ── */}
      {step === 0 && (
        <div className="card p-5 space-y-5">
          <h3 className="text-[15px] font-bold text-[var(--text-primary)]">Select Pay Period</h3>
          <div className="space-y-1.5 max-w-xs">
            <label className="text-[12px] font-semibold text-[var(--text-secondary)]">Month</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} className={inp} max={format(new Date(), 'yyyy-MM')}/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[['Pay Period', payPeriod], ['From', periodStart], ['To', periodEnd]].map(([l, v]) => (
              <div key={l} className="p-3 bg-[var(--bg-muted)] rounded-[10px]">
                <p className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide">{l}</p>
                <p className="text-[13px] font-semibold text-[var(--text-primary)] mt-0.5">{v}</p>
              </div>
            ))}
          </div>
          <Button onClick={() => setStep(1)} icon={ArrowRight}>Next: Select Employees</Button>
        </div>
      )}

      {/* ── STEP 1: Employee selection ── */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="card p-5 space-y-4">
            <h3 className="text-[15px] font-bold text-[var(--text-primary)]">Select Employees</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[['all', Users, 'All Employees'], ['department', Building2, 'By Department'], ['designation', User, 'By Designation'], ['custom', Check, 'Custom Select']].map(([v, Icon, label]) => (
                <button key={v} onClick={() => setSelection(v)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-[10px] border text-[12px] font-semibold transition-all ${selection === v ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]' : 'border-[var(--border)] hover:border-[var(--accent)]/50 text-[var(--text-secondary)]'}`}>
                  <Icon size={18}/>{label}
                </button>
              ))}
            </div>

            {selection === 'department' && (
              <div className="space-y-1.5">
                <label className="text-[12px] font-semibold text-[var(--text-secondary)]">Department</label>
                <select className={inp} value={selDept} onChange={e => setSelDept(e.target.value)}>
                  <option value="">— All departments —</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}

            {selection === 'designation' && (
              <div className="space-y-1.5">
                <label className="text-[12px] font-semibold text-[var(--text-secondary)]">Designation</label>
                <select className={inp} value={selDesig} onChange={e => setSelDesig(e.target.value)}>
                  <option value="">— All designations —</option>
                  {allDesignations.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}

            {selection === 'custom' && (
              <div className="space-y-2 max-h-64 overflow-y-auto border border-[var(--border)] rounded-[10px] p-2">
                {employees.map(e => {
                  const id = e.id || e.employee_id;
                  const hasSS = !!ssMap[id];
                  return (
                    <label key={id} className={`flex items-center gap-3 p-2 rounded-[8px] cursor-pointer hover:bg-[var(--bg-muted)] ${!hasSS ? 'opacity-50' : ''}`}>
                      <input type="checkbox" checked={customIds.has(id)} onChange={() => hasSS && toggleCustom(id)} disabled={!hasSS} className="w-4 h-4 accent-[var(--accent)]"/>
                      <Avatar name={`${e.first_name} ${e.last_name}`} size="xs"/>
                      <span className="text-[13px] text-[var(--text-primary)] flex-1">{e.first_name} {e.last_name}</span>
                      {!hasSS && <span className="text-[10px] text-amber-500 font-semibold">No salary</span>}
                    </label>
                  );
                })}
              </div>
            )}

            {/* Summary */}
            <div className="flex flex-wrap gap-4 pt-2 border-t border-[var(--border)] text-[12px]">
              <span className="text-[var(--text-secondary)]">Selected: <b className="text-[var(--text-primary)]">{selectedEmployees.length}</b></span>
              <span className="text-green-600">With salary: <b>{withSS.length}</b></span>
              {withoutSS.length > 0 && <span className="text-amber-600">Missing salary: <b>{withoutSS.length}</b> (will be skipped)</span>}
            </div>
          </div>

          {withoutSS.length > 0 && (
            <div className="p-3 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-[12px] text-amber-700">
              <b>{withoutSS.map(e => `${e.first_name} ${e.last_name}`).join(', ')}</b> will be skipped — no salary structure.
              <button onClick={() => navigate('/payroll/salary-structures')} className="underline ml-1">Set up →</button>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(0)}>← Back</Button>
            <Button onClick={() => setStep(2)} disabled={withSS.length === 0} icon={ArrowRight}>
              Next: Review ({withSS.length} employees)
            </Button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Review & Run ── */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Aggregate summary */}
          <div className="card p-5 space-y-4">
            <h3 className="text-[15px] font-bold text-[var(--text-primary)]">Payroll Summary — {payPeriod}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                ['Employees', withSS.length, 'text-[var(--text-primary)]'],
                ['Total Gross', formatCurrency(totals.gross), 'text-[var(--text-primary)]'],
                ['Total PF', formatCurrency(totals.pf), 'text-red-500'],
                ['ESIC + PT', formatCurrency(totals.esic + totals.pt), 'text-red-500'],
                ['Net Payable', formatCurrency(totals.net), 'text-green-600'],
              ].map(([l, v, cls]) => (
                <div key={l} className="p-3 bg-[var(--bg-muted)] rounded-[10px]">
                  <p className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide">{l}</p>
                  <p className={`text-[15px] font-bold mt-0.5 ${cls}`}>{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Per-employee breakdown */}
          <div className="card p-0 overflow-x-auto">
            <div className="px-5 py-3 border-b border-[var(--border)]">
              <p className="text-[13px] font-semibold text-[var(--text-primary)]">Employee Breakdown</p>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  {['Employee','Gross','PF','ESIC','PT','Net Pay'].map(h=>(
                    <th key={h} className="text-left text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {withSS.map(e => {
                  const id = e.id || e.employee_id;
                  const ss = ssMap[id];
                  const gross = Number(ss.gross_monthly);
                  const basic = Number(ss.basic);
                  const pfEmp = ss.pf_applicable ? Math.round(Math.min(basic, 15000) * 0.12) : 0;
                  const esic  = gross <= 21000 ? Math.round(gross * 0.0075) : 0;
                  const pt    = gross > 10000 ? 200 : gross > 7500 ? 175 : 0;
                  const net   = gross - pfEmp - esic - pt;
                  return (
                    <tr key={id} className="hover:bg-[var(--bg-muted)]">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={`${e.first_name} ${e.last_name}`} size="xs"/>
                          <div>
                            <p className="text-[13px] font-semibold text-[var(--text-primary)]">{e.first_name} {e.last_name}</p>
                            <p className="text-[10px] text-[var(--text-tertiary)]">{ss.department}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-[13px]">{formatCurrency(gross)}</td>
                      <td className="px-5 py-3 text-[13px] text-red-500">−{formatCurrency(pfEmp)}</td>
                      <td className="px-5 py-3 text-[13px] text-red-500">−{formatCurrency(esic)}</td>
                      <td className="px-5 py-3 text-[13px] text-red-500">−{formatCurrency(pt)}</td>
                      <td className="px-5 py-3 text-[13px] font-bold text-green-600">{formatCurrency(net)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card p-4 border-2 border-[var(--accent)]/30 bg-[var(--accent)]/5 space-y-2">
            <p className="text-[13px] font-bold text-[var(--text-primary)]">Before you run:</p>
            <ul className="text-[12px] text-[var(--text-secondary)] space-y-1">
              <li>✓ Review attendance for {payPeriod} is complete</li>
              <li>✓ Leave applications are approved</li>
              <li>✓ No salary revisions pending for this month</li>
              <li>✓ LOP will be calculated from attendance data automatically</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
            <Button onClick={handleRun} loading={runPayroll.isPending} icon={Play}>
              Process Payroll for {payPeriod}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
